import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { LogOut, RefreshCw, Lock, Users, Heart, MessageCircle, Download, Pencil, Trash2, X, Check } from "lucide-react";
import { API } from "@/lib/api";

const TOKEN_KEY = "wedding_admin_token";

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtDate(s) {
  try {
    const d = typeof s === "string" ? new Date(s) : s;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function downloadCSV(filename, rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(",")]
    .concat(rows.map((r) => headers.map((h) => esc(r[h])).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------- Edit Modal ----------

function EditModal({ title, children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-[#FBF5EA] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-8 pt-8 pb-4 border-b border-[#EFE6D2]">
          <p className="overline" style={{ color: "var(--wb-primary)" }}>{title}</p>
          <button onClick={onClose} className="hover:rotate-90 transition-transform duration-200">
            <X size={18} />
          </button>
        </div>
        <div className="px-8 py-8">{children}</div>
      </div>
    </div>
  );
}

// ---------- RSVP Edit Form ----------

function RSVPEditForm({ rsvp, onSave, onClose }) {
  const [form, setForm] = useState({
    name: rsvp.name ?? "",
    email: rsvp.email ?? "",
    phone: rsvp.phone ?? "",
    attending: rsvp.attending ?? "yes",
    events: rsvp.events ?? [],
    guests: rsvp.guests ?? 1,
    dietary: rsvp.dietary ?? "",
    message: rsvp.message ?? "",
  });
  const [saving, setSaving] = useState(false);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEvent = (e) =>
    setForm((f) => ({
      ...f,
      events: f.events.includes(e) ? f.events.filter((x) => x !== e) : [...f.events, e],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/rsvps/${rsvp.id}`,
        { ...form, guests: Number(form.guests) },
        { headers: authHeaders() }
      );
      toast.success("RSVP updated.");
      onSave(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="overline block mb-1">Name</label>
          <input className="wb-input" value={form.name} onChange={(e) => up("name", e.target.value)} />
        </div>
        <div>
          <label className="overline block mb-1">Email</label>
          <input type="email" className="wb-input" value={form.email} onChange={(e) => up("email", e.target.value)} />
        </div>
        <div>
          <label className="overline block mb-1">Phone</label>
          <input className="wb-input" value={form.phone} onChange={(e) => up("phone", e.target.value)} />
        </div>
        <div>
          <label className="overline block mb-1">Guests</label>
          <input type="number" min="0" max="20" className="wb-input" value={form.guests} onChange={(e) => up("guests", e.target.value)} />
        </div>
      </div>

      <div>
        <label className="overline block mb-2">Attending</label>
        <div className="flex gap-3">
          {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }].map((o) => (
            <button
              type="button"
              key={o.v}
              onClick={() => up("attending", o.v)}
              className={`px-4 py-2 text-[0.72rem] uppercase tracking-[0.18em] border transition-all ${
                form.attending === o.v
                  ? "bg-[#7A1F2B] text-white border-[#7A1F2B]"
                  : "bg-transparent border-[#EFE6D2] hover:border-[#7A1F2B]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {form.attending === "yes" && (
        <div>
          <label className="overline block mb-2">Events</label>
          <div className="space-y-1">
            {[{ v: "wedding", l: "Wedding · 24 Jun 2026" }, { v: "reception", l: "Reception · 27 Jun 2026" }].map((ev) => (
              <label key={ev.v} className="wb-check">
                <input type="checkbox" checked={form.events.includes(ev.v)} onChange={() => toggleEvent(ev.v)} />
                <span>{ev.l}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="overline block mb-1">Dietary</label>
        <input className="wb-input" value={form.dietary} onChange={(e) => up("dietary", e.target.value)} />
      </div>
      <div>
        <label className="overline block mb-1">Message</label>
        <textarea className="wb-input" rows={3} value={form.message} onChange={(e) => up("message", e.target.value)} />
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onClose} className="text-[0.72rem] uppercase tracking-[0.18em] border border-[#EFE6D2] px-5 py-2 hover:border-[#7A1F2B] transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="wb-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

// ---------- Guestbook Edit Form ----------

function GuestbookEditForm({ entry, onSave, onClose }) {
  const [name, setName] = useState(entry.name ?? "");
  const [message, setMessage] = useState(entry.message ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/guestbook/${entry.id}`,
        { name, message },
        { headers: authHeaders() }
      );
      toast.success("Message updated.");
      onSave(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <label className="overline block mb-1">Name</label>
        <input className="wb-input" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <label className="overline block mb-1">Message</label>
        <textarea className="wb-input" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onClose} className="text-[0.72rem] uppercase tracking-[0.18em] border border-[#EFE6D2] px-5 py-2 hover:border-[#7A1F2B] transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="wb-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

// ---------- Login ----------

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter the password.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#FBF5EA" }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Lock size={20} className="mx-auto" style={{ color: "var(--wb-primary)" }} />
          <h1 className="font-serif-wb text-4xl sm:text-5xl mt-5 tracking-tight">
            Admin <span className="italic" style={{ color: "var(--wb-primary)" }}>access</span>
          </h1>
          <p className="mt-4 text-sm text-[#5C5C5C]">
            Sign in to view RSVPs and guestbook messages.
          </p>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="bg-white border border-[#EFE6D2] p-10">
          <label className="overline block mb-2">Shared Password</label>
          <input
            type="password"
            className="wb-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            placeholder="Enter password"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="wb-btn mt-8 w-full justify-center"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <a
          href="/"
          data-testid="admin-back-home"
          className="block text-center mt-8 text-[0.74rem] uppercase tracking-[0.22em] text-[#5C5C5C] hover:text-[var(--wb-primary)]"
        >
          ← Back to home
        </a>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#EFE6D2] p-6">
      <p className="overline" style={{ color: "var(--wb-primary)" }}>{label}</p>
      <p className="font-serif-wb text-4xl mt-3 tracking-tight">{value ?? "—"}</p>
    </div>
  );
}

// ---------- Dashboard ----------

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("rsvps");
  const [rsvps, setRsvps] = useState([]);
  const [guestbook, setGuestbook] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // edit/delete state
  const [editingRsvp, setEditingRsvp] = useState(null);
  const [deletingRsvpId, setDeletingRsvpId] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, g, s] = await Promise.all([
        axios.get(`${API}/admin/rsvps`, { headers: authHeaders() }),
        axios.get(`${API}/admin/guestbook`, { headers: authHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setRsvps(r.data || []);
      setGuestbook(g.data || []);
      setStats(s.data || null);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  };

  const exportRsvps = () => {
    downloadCSV(
      `rsvps-${new Date().toISOString().slice(0, 10)}.csv`,
      rsvps.map((r) => ({ ...r, events: (r.events || []).join("|") })),
      ["name", "email", "phone", "attending", "events", "guests", "dietary", "message", "created_at"]
    );
  };

  // --- RSVP CRUD ---
  const handleRsvpSaved = (updated) => {
    setRsvps((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setEditingRsvp(null);
  };

  const confirmDeleteRsvp = async (id) => {
    try {
      await axios.delete(`${API}/admin/rsvps/${id}`, { headers: authHeaders() });
      setRsvps((prev) => prev.filter((r) => r.id !== id));
      setStats((s) => s ? { ...s, rsvps_total: (s.rsvps_total || 1) - 1 } : s);
      toast.success("RSVP deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingRsvpId(null);
    }
  };

  // --- Guestbook CRUD ---
  const handleEntrySaved = (updated) => {
    setGuestbook((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    setEditingEntry(null);
  };

  const confirmDeleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/guestbook/${id}`, { headers: authHeaders() });
      setGuestbook((prev) => prev.filter((g) => g.id !== id));
      setStats((s) => s ? { ...s, guestbook_messages: (s.guestbook_messages || 1) - 1 } : s);
      toast.success("Message deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingEntryId(null);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ background: "#FBF5EA" }}>
      {/* Edit modals */}
      {editingRsvp && (
        <EditModal title="Edit RSVP" onClose={() => setEditingRsvp(null)}>
          <RSVPEditForm rsvp={editingRsvp} onSave={handleRsvpSaved} onClose={() => setEditingRsvp(null)} />
        </EditModal>
      )}
      {editingEntry && (
        <EditModal title="Edit Message" onClose={() => setEditingEntry(null)}>
          <GuestbookEditForm entry={editingEntry} onSave={handleEntrySaved} onClose={() => setEditingEntry(null)} />
        </EditModal>
      )}

      {/* Top bar */}
      <header className="border-b border-[#EFE6D2] bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock size={16} style={{ color: "var(--wb-primary)" }} />
            <p className="overline">Admin · Varsha &amp; Parag</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="p-2 hover:text-[var(--wb-primary)] transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.22em] border-b border-[#1A1A1A] pb-0.5 hover:text-[var(--wb-primary)] hover:border-[var(--wb-primary)] transition-colors"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
        <h1 className="font-serif-wb text-4xl sm:text-5xl tracking-tight">
          Wedding <span className="italic" style={{ color: "var(--wb-primary)" }}>dashboard</span>
        </h1>
        <p className="mt-3 text-sm text-[#5C5C5C]">
          {loading ? "Loading…" : `${rsvps.length} RSVPs · ${guestbook.length} messages`}
        </p>

        {/* Stats */}
        <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-10">
          <StatCard testid="stat-rsvps-total" label="RSVPs" value={stats?.rsvps_total} />
          <StatCard testid="stat-attending" label="Attending" value={stats?.rsvps_attending} />
          <StatCard testid="stat-declined" label="Declined" value={stats?.rsvps_declined} />
          <StatCard testid="stat-guests" label="Total guests" value={stats?.guests_total} />
          <StatCard testid="stat-wedding" label="Wedding" value={stats?.wedding_attendees} />
          <StatCard testid="stat-reception" label="Reception" value={stats?.reception_attendees} />
        </div>

        {/* Tabs */}
        <div className="mt-12 border-b border-[#EFE6D2] flex items-center gap-1 sm:gap-2">
          {[
            { k: "rsvps", l: `RSVPs (${rsvps.length})`, Icon: Users },
            { k: "guestbook", l: `Guestbook (${guestbook.length})`, Icon: MessageCircle },
          ].map(({ k, l, Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 text-[0.74rem] uppercase tracking-[0.22em] transition-colors border-b-2 ${
                tab === k
                  ? "border-[var(--wb-primary)] text-[var(--wb-primary)]"
                  : "border-transparent text-[#5C5C5C] hover:text-[var(--wb-text)]"
              }`}
            >
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>

        {/* Content */}
        <section className="mt-8">
          {tab === "rsvps" ? (
            <div data-testid="admin-rsvps-table">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#5C5C5C]">Newest first</p>
                <button
                  onClick={exportRsvps}
                  data-testid="admin-export-csv"
                  disabled={!rsvps.length}
                  className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.22em] border border-[#EFE6D2] px-4 py-2 hover:border-[var(--wb-primary)] hover:text-[var(--wb-primary)] transition-colors disabled:opacity-50"
                >
                  <Download size={13} /> Export CSV
                </button>
              </div>

              {loading ? (
                <p className="text-[#5C5C5C]">Loading…</p>
              ) : rsvps.length === 0 ? (
                <div className="bg-white border border-[#EFE6D2] p-10 text-center">
                  <Heart size={20} className="mx-auto" style={{ color: "var(--wb-primary)" }} />
                  <p className="font-serif-wb italic text-2xl mt-3 text-[#5C5C5C]">No RSVPs yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto bg-white border border-[#EFE6D2]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5EBD3]">
                      <tr className="text-left">
                        {["Name", "Email", "Attending", "Events", "Guests", "Dietary", "Message", "When", ""].map((h, i) => (
                          <th
                            key={i}
                            className="px-4 py-3 overline whitespace-nowrap"
                            style={{ color: "var(--wb-primary)" }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rsvps.map((r, i) => (
                        <tr
                          key={r.id || i}
                          data-testid={`admin-rsvp-row-${i}`}
                          className="border-t border-[#EFE6D2] align-top"
                        >
                          <td className="px-4 py-3 font-medium whitespace-nowrap">{r.name}</td>
                          <td className="px-4 py-3 text-[#5C5C5C] whitespace-nowrap">{r.email}</td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-block px-2 py-0.5 text-[0.65rem] uppercase tracking-[0.18em]"
                              style={{
                                background: r.attending === "yes" ? "#E8902A22" : "#7A1F2B22",
                                color: r.attending === "yes" ? "#8a4a0a" : "#7A1F2B",
                              }}
                            >
                              {r.attending}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#5C5C5C] whitespace-nowrap">
                            {(r.events || []).join(", ") || "—"}
                          </td>
                          <td className="px-4 py-3">{r.guests ?? 0}</td>
                          <td className="px-4 py-3 text-[#5C5C5C] max-w-[180px]">{r.dietary || "—"}</td>
                          <td className="px-4 py-3 text-[#5C5C5C] max-w-[280px]">{r.message || "—"}</td>
                          <td className="px-4 py-3 text-[#5C5C5C] whitespace-nowrap text-xs">
                            {fmtDate(r.created_at)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {deletingRsvpId === r.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-[#7A1F2B]">Delete?</span>
                                <button
                                  onClick={() => confirmDeleteRsvp(r.id)}
                                  className="text-[#7A1F2B] hover:opacity-70"
                                  title="Confirm delete"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={() => setDeletingRsvpId(null)}
                                  className="text-[#5C5C5C] hover:opacity-70"
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setEditingRsvp(r)}
                                  data-testid={`admin-rsvp-edit-${i}`}
                                  className="text-[#5C5C5C] hover:text-[var(--wb-primary)] transition-colors"
                                  title="Edit"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => setDeletingRsvpId(r.id)}
                                  data-testid={`admin-rsvp-delete-${i}`}
                                  className="text-[#5C5C5C] hover:text-[#7A1F2B] transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div data-testid="admin-guestbook-list" className="space-y-4">
              {loading ? (
                <p className="text-[#5C5C5C]">Loading…</p>
              ) : guestbook.length === 0 ? (
                <div className="bg-white border border-[#EFE6D2] p-10 text-center">
                  <MessageCircle size={20} className="mx-auto" style={{ color: "var(--wb-primary)" }} />
                  <p className="font-serif-wb italic text-2xl mt-3 text-[#5C5C5C]">No messages yet.</p>
                </div>
              ) : (
                guestbook.map((g, i) => (
                  <div
                    key={g.id || i}
                    data-testid={`admin-guestbook-row-${i}`}
                    className="bg-white border border-[#EFE6D2] p-6"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="font-serif-wb text-lg">{g.name}</p>
                          <span className="text-xs text-[#a39a89]">{fmtDate(g.created_at)}</span>
                        </div>
                        <p className="mt-2 text-[#1A1A1A] leading-relaxed">{g.message}</p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {deletingEntryId === g.id ? (
                          <>
                            <span className="text-xs text-[#7A1F2B]">Delete?</span>
                            <button
                              onClick={() => confirmDeleteEntry(g.id)}
                              className="text-[#7A1F2B] hover:opacity-70"
                              title="Confirm"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setDeletingEntryId(null)}
                              className="text-[#5C5C5C] hover:opacity-70"
                              title="Cancel"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditingEntry(g)}
                              data-testid={`admin-guestbook-edit-${i}`}
                              className="text-[#5C5C5C] hover:text-[var(--wb-primary)] transition-colors"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => setDeletingEntryId(g.id)}
                              data-testid={`admin-guestbook-delete-${i}`}
                              className="text-[#5C5C5C] hover:text-[#7A1F2B] transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/admin/verify`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBF5EA" }}>
        <p className="overline">Checking session…</p>
      </div>
    );
  }

  return (
    <>
      {authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
      ) : (
        <LoginScreen onSuccess={() => setAuthed(true)} />
      )}
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#4A1018",
            color: "#FBF5EA",
            border: "1px solid #4A1018",
            borderRadius: "2px",
            fontFamily: "Outfit, sans-serif",
            letterSpacing: "0.04em",
          },
        }}
      />
    </>
  );
}
